/**
 * Single source of truth for "is debug mode on".
 *
 * Anything that wants verbose behaviour — the dev debug panel, consent tracing,
 * a library's `debug` option — reads this rather than re-deriving it. Separate
 * copies drift, and then turning debug on for one surface silently fails to
 * turn it on for the next.
 */

export const DEBUG_LOCAL_STORAGE_KEY = 'app::debug'
export const DEBUG_QUERY_PARAM = 'debug'

let cached: boolean | undefined

function readStored(): boolean {
  try {
    return window.localStorage.getItem(DEBUG_LOCAL_STORAGE_KEY) === 'true'
  } catch {
    // Storage access throws outright in some privacy modes. Debug mode is never
    // worth failing a page load over.
    return false
  }
}

function writeStored(enabled: boolean): void {
  try {
    if (enabled) {
      window.localStorage.setItem(DEBUG_LOCAL_STORAGE_KEY, 'true')
    } else {
      window.localStorage.removeItem(DEBUG_LOCAL_STORAGE_KEY)
    }
  } catch {
    // Same as above — the query param still applies for this pageview.
  }
}

function resolve(): boolean {
  if (import.meta.dev) return true
  if (typeof window === 'undefined') return false

  const param = new URLSearchParams(window.location.search).get(
    DEBUG_QUERY_PARAM
  )

  // `?debug` / `?debug=true` turns it on and remembers, so it survives the
  // client-side navigation that drops the query string. `?debug=false` is the
  // way back off.
  if (param !== null) {
    const enabled = param !== 'false' && param !== '0'
    writeStored(enabled)
    return enabled
  }

  return readStored()
}

/**
 * Resolved once per pageview: a value that flipped mid-session would leave
 * some callers tracing and others silent.
 */
export function isDebugEnabled(): boolean {
  cached ??= resolve()
  return cached
}

export function resetDebugModeForTests(): void {
  cached = undefined
}
